import { ReactNode } from 'react';
import { When } from 'react-if';
import { useResizeObserver } from '@toss/react';
import cns from 'classnames';
import { useTranslation } from 'next-i18next';
import { CheckSmallCircleColorIcon, PersonStackFillIcon } from '@zep/icons';
import { Button } from '@zep/ui';
import { PlanProduct, SubscribePlan } from '../../../../../types';
import { FcurrencyByUnitOptions } from '../../../../../utils';
import { SubscriptionTKey } from '../../paymentForm/schema';
import { usePricingHoverButton } from '../hooks';
import { usePricing } from '../PricingProvider';
import S from './PricingCardLegacy.module.scss';
export const PricingCardText = ({
  price,
  plan,
  discountRate,
  originalPrice,
  isMonthly,
  fCurrencyByUnitOptions,
  isSaleExist
}: {
  price: string;
  plan?: PlanProduct;
  discountRate?: string;
  originalPrice?: string;
  isMonthly?: boolean;
  fCurrencyByUnitOptions?: FcurrencyByUnitOptions;
  isSaleExist: boolean;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  // 높이 동기화 로직은 주석 처리된 채 남아 있었고, 계산된 targetHeight 는 아무 데도
  // 쓰이지 않으면서 리사이즈마다 getComputedStyle 을 호출하고 있었다. 계산은 제거하고
  // ref 는 DOM 참조용으로만 남긴다(복원이 필요하면 git 히스토리 참조).
  const priceTextRef = useResizeObserver<HTMLDivElement>(() => {});
  return <div className={S.price} data-sentry-component="PricingCardText" data-sentry-source-file="PricingCardLegacy.tsx">
      <div>
        <div className={S.text} ref={priceTextRef}>
          {originalPrice && discountRate && <span className={S.text_sale_price}>
              {originalPrice}
              {plan?.currency === 'KRW' && fCurrencyByUnitOptions?.currencyString}
            </span>}
          <div>
            <strong className={cns(S.text_price, {
            [S.text_normal]: !plan
          })}>
              {price}
            </strong>
            {plan?.currency === 'KRW' && <span className={S.text_currency}>
                {fCurrencyByUnitOptions?.currencyString}
              </span>}
            {isMonthly && <span className={S.text_month}>
                /{t('subscription:pricing.plansCommon.monthly')}
              </span>}
          </div>
        </div>
      </div>
      {isSaleExist && <div className={S.discount_contents}>
          {discountRate && <>
              <span className={S.text_discount_rate}>{discountRate}</span>
              {plan?.saleType && <SaleLabel saleType={plan?.saleType as SaleType} />}
            </>}
        </div>}
    </div>;
};
const SaleLabel = ({
  saleType
}: {
  saleType: SaleType;
}) => {
  // @ts-ignore
  const {
    cardStyles,
    setCardTextHeight
  } = usePricing();
  const {
    t
  } = useTranslation(['subscription']);
  const saleTypeKey: `subscription:pricing.saleType.${SaleType}` = `subscription:pricing.saleType.${saleType}`;
  const discountTextRef = useResizeObserver<HTMLDivElement>(entry => {
    const targetHeight = parseInt(window?.getComputedStyle(entry.target as HTMLDivElement).height);
    if (targetHeight !== cardStyles.maxDiscountTextHeight) {
      setCardTextHeight('discount', targetHeight);
    }
  });
  return <div className={S.price_discount_content} ref={discountTextRef} data-sentry-component="SaleLabel" data-sentry-source-file="PricingCardLegacy.tsx">
      {t(saleTypeKey)}
    </div>;
};
export const PricingCardActionDisabledText = ({
  children
}: {
  children: ReactNode;
}) => {
  return <p className={S.footer_text} data-sentry-component="PricingCardActionDisabledText" data-sentry-source-file="PricingCardLegacy.tsx">{children}</p>;
};
export const PricingCardButton = ({
  isFree,
  children,
  onClick,
  planType,
  disabled
}: {
  isFree?: boolean;
  children: ReactNode;
  onClick?: () => void;
  planType?: SubscribePlan;
  disabled?: boolean;
}) => {
  const {
    hoverStates,
    pricingButtonStyle,
    handleMouseEnter,
    handleMouseLeave
  } = usePricingHoverButton();
  if (planType === 'free' || isFree) {
    return <Button variant={'sub'} size={'md'} disabled={disabled} onClick={onClick} isFullWidth>
        {children}
      </Button>;
  }
  return <Button style={!disabled && {
    // @ts-ignore
    backgroundColor: hoverStates[planType] ?
    // @ts-ignore
    pricingButtonStyle[planType]?.backgroundHoverColor :
    // @ts-ignore
    pricingButtonStyle[planType]?.backgroundColor
  } || {}} onMouseEnter={() => handleMouseEnter(
  // @ts-ignore
  planType)} onMouseLeave={() => handleMouseLeave(
  // @ts-ignore
  planType)} variant={disabled ? 'sub' : 'primary'} size={'md'} onClick={onClick} disabled={disabled} isFullWidth data-sentry-element="Button" data-sentry-component="PricingCardButton" data-sentry-source-file="PricingCardLegacy.tsx">
      ]{children}
    </Button>;
};
export const PricingCardLegacy = (props: PricingCardProps) => {
  const {
    highlight,
    type = 'free',
    priceText,
    connectionLimit,
    originalConnectionLimit,
    monthlyRatePerPerson,
    disabled,
    features,
    children,
    footer
  } = props;
  const {
    t
  } = useTranslation(['subscription']);
  return <div className={cns(S.card, [S[type], {
    [S.highlight]: highlight,
    [S.disabled]: disabled
  }])} data-sentry-component="PricingCardLegacy" data-sentry-source-file="PricingCardLegacy.tsx">
      {disabled && <div className={S.dimmed}></div>}

      <div className={S.top_line}></div>

      <div className={S.header}>
        <div className={S.title_wrapper}>
          <h2 className={S.title}>{type.capitalize()}</h2>
          {highlight && <div className={S.highlight_text}>
              {t('subscription:pricing.plansPro.reccommendationMesseage')}
            </div>}
        </div>

        {priceText}

        <div className={S.action_button}>{children}</div>

        <div className={S.concurrent}>
          <dl>
            <dt>
              <PersonStackFillIcon width={16} height={16} data-sentry-element="PersonStackFillIcon" data-sentry-source-file="PricingCardLegacy.tsx" />
              <span>{t('subscription:pricing.plansCommon.ccu')}</span>
            </dt>
            {originalConnectionLimit && originalConnectionLimit !== connectionLimit && <del>{originalConnectionLimit}</del>}
            <dd>{connectionLimit}</dd>
          </dl>
          {/*<When condition={type === 'free'}>*/}
          {/*  <div className={S.concurrent_free_nofify}>*/}
          {/*    <span className={S.icon}>*/}
          {/*      <ExclamationmarkSmallCircleIcon*/}
          {/*        width={16}*/}
          {/*        height={16}*/}
          {/*        fill={Colors.RedDefault}*/}
          {/*      />*/}
          {/*    </span>*/}
          {/*    <span className={S.message}>*/}
          {/*      {t('subscription:pricing.selectPlan.freePlanNotify')}*/}
          {/*    </span>*/}
          {/*  </div>*/}
          {/*</When>*/}
          <div className={S.concurrent_text}>{monthlyRatePerPerson}</div>
        </div>
      </div>

      <div className={S.content}>
        {features && <ul className={S.features}>
            <When condition={type === 'enterprise'}>
              <h3 className={S.bold}>
                {t('subscription:payment.planSelect.featureProAll')}
              </h3>
            </When>
            <When condition={type === 'pro'}>
              <h3 className={S.bold}>
                {t('subscription:payment.planSelect.featureBasicAll')}
              </h3>
            </When>
            {features.map((feature, index) => <li key={index}>
                <CheckSmallCircleColorIcon width={16} height={16} />
                <span>{t(feature)}</span>
              </li>)}
          </ul>}
      </div>
      {footer}
    </div>;
};
type PricingCardProps = {
  highlight?: boolean;
  type: SubscribePlan;
  priceText: ReactNode;
  connectionLimit: string;
  originalConnectionLimit?: string;
  monthlyRatePerPerson?: string;
  features?: SubscriptionTKey[];
  children?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  footer?: ReactNode;
};
type SaleType = 'annuallyPayment' | 'education' | 'educationAnnuallyPayment';